import React from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { proxifyImage } from '../../lib/imageProxy';
import { cityCoords } from '../../lib/cityCoords';

// Generic right-side detail drawer for event/festival/venue records extracted
// directly from MCP tool results. Renders a title, optional image, an optional
// "about" paragraph, tags, and humanized key/value fields — so it works for
// the heterogeneous shapes (Soundcharts festivals, live events, directory
// records, DMS rows) without knowing each field name in advance.

const SKIP_KEYS = new Set([
  'name', 'n', 'title', 'uuid', 'id', 'identifier', '@id', '@type',
  'image', 'imageUrl', 'image_url', 'img', 'photo', 'slug', 'url',
  'appUrl', 'webUrl', 'about', 'description', 'short_description',
  'summary', 'tags', 'kv', 'type', 'Type', 'source',
  'datePublished', 'dateModified',
  'lat', 'lng', 'latitude', 'longitude',
]);

function humanize(key) {
  return String(key)
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function isUrl(value) {
  return typeof value === 'string' && /^https?:\/\//i.test(value.trim());
}

function isImageUrl(value) {
  return isUrl(value) && /\.(jpe?g|png|gif|webp|svg|avif)([?#].*)?$/i.test(value.trim());
}

const IMAGE_KEY_RE = /(cover|logo|poster|thumbnail|avatar|banner|image|img|photo|picture|pic)/i;

const DESCRIPTION_KEYS = ['about', 'description', 'short_description', 'summary'];

function descriptionOf(record) {
  for (const k of DESCRIPTION_KEYS) {
    const v = record && record[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

function imageOf(record) {
  const url =
    (record &&
      (record.imageUrl || record.image_url || record.image || record.img || record.photo ||
        record.cover_image || record.coverImage || record.event_logo || record.eventLogo ||
        record.logo || record.poster || record.thumbnail || record.thumb)) ||
    '';
  return proxifyImage(url);
}

function flattenRecord(record) {
  const flat = [];
  const images = [];
  const links = [];
  if (!record || typeof record !== 'object') return { flat, images, links };
  for (const [key, value] of Object.entries(record)) {
    if (SKIP_KEYS.has(key)) continue;
    if (value == null || value === '') continue;
    if (Array.isArray(value)) {
      if (value.length && value.every((v) => typeof v !== 'object' || v === null)) {
        flat.push([humanize(key), value.map(String).join(', ')]);
      }
      continue;
    }
    if (typeof value === 'object') continue;
    const str = String(value).trim();
    if (isImageUrl(str) || (isUrl(str) && IMAGE_KEY_RE.test(key))) {
      images.push({ label: humanize(key), url: str });
      continue;
    }
    if (isUrl(str)) {
      links.push({ label: humanize(key), url: str });
      continue;
    }
    flat.push([humanize(key), str]);
  }
  return { flat, images, links };
}

const GREEN = '#00dc49';

function venueCoords(record) {
  if (!record || typeof record !== 'object') return null;
  const lat = record.lat ?? record.latitude;
  const lng = record.lng ?? record.longitude;
  if (lat != null && lng != null && !Number.isNaN(Number(lat)) && !Number.isNaN(Number(lng))) {
    return { lat: Number(lat), lng: Number(lng) };
  }
  const city = record.city || record.hq_city || record.City;
  const c = cityCoords(city);
  return c ? { lat: c[0], lng: c[1] } : null;
}

function pinIcon() {
  return L.divIcon({
    className: 'fmap-pin',
    html:
      '<span style="display:block;width:16px;height:16px;border-radius:50%;background:' +
      GREEN +
      ';border:2px solid #fff;box-shadow:0 0 0 2px rgba(0,0,0,.4),0 0 8px rgba(0,220,73,.9)"></span>',
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });
}

function MapReady() {
  const map = useMap();
  React.useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 60);
    return () => clearTimeout(t);
  }, [map]);
  return null;
}

function VenueMap({ lat, lng, name }) {
  return (
    <MapContainer
      center={[lat, lng]}
      zoom={12}
      scrollWheelZoom={false}
      style={{ height: 220, width: '100%' }}>
      <TileLayer
        attribution="Tiles &copy; Esri"
        url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}"
        maxZoom={16}
      />
      <MapReady />
      <Marker position={[lat, lng]} icon={pinIcon()}>
        <Popup>
          <b>{name}</b>
        </Popup>
      </Marker>
    </MapContainer>
  );
}

export default function RecordDetailDrawer({ title, record, type, onClose }) {
  const name = title || (record && (record.name || record.n || record.title)) || 'Detail';
  const about = descriptionOf(record);
  const tags = record && Array.isArray(record.tags) ? record.tags : [];
  const kvRows =
    record && Array.isArray(record.kv)
      ? record.kv.filter((r) => Array.isArray(r) && r[0] && r[1] && r[1] !== '—')
      : [];
  const { flat: flatRows, links } = flattenRecord(record);
  const coords = type === 'venue' ? venueCoords(record) : null;
  const heroImage = imageOf(record);

  const sub = type ? (type === 'festival' ? 'Festival' : type === 'venue' ? 'Venue' : 'Event') : '';

  return (
    <div className="ea-root fdrawer">
      <div className="bg" onClick={onClose} />
      <div className="panel">
        <div className="fhead">
          <div>
            <h3 className="ftitle">{name}</h3>
            {sub && <div className="fsub">{sub}</div>}
          </div>
          <button className="fclose" onClick={onClose}>
            Close ✕
          </button>
        </div>

        {heroImage && (
          <div className="fhero">
            <img src={heroImage} alt={name} onError={(e) => (e.currentTarget.style.display = 'none')} />
          </div>
        )}

        {about && <p className="fdesc">{about}</p>}

        {coords && (
          <div className="fsec">
            <div className="fsec-h">Location</div>
            <div className="fmap-wrap">
              <VenueMap lat={coords.lat} lng={coords.lng} name={name} />
            </div>
          </div>
        )}

        {links.length > 0 && (
          <div className="fsec">
            <div className="fsec-h">Links</div>
            <div className="links">
              {links.map((l, i) => (
                <a key={i} className="linkbtn" href={l.url} target="_blank" rel="noopener noreferrer">
                  {l.label} <span className="linkbtn-arrow">↗</span>
                </a>
              ))}
            </div>
          </div>
        )}

        {(kvRows.length > 0 || flatRows.length > 0) && (
          <div className="fsec">
            <div className="fsec-h">Details</div>
            {kvRows.map(([k, v], i) => (
              <div className="fkv" key={'k' + i}>
                <span className="k">{k}</span>
                <span className="v">{String(v)}</span>
              </div>
            ))}
            {flatRows.map(([k, v], i) => (
              <div className="fkv" key={'f' + i}>
                <span className="k">{k}</span>
                <span className="v">{v}</span>
              </div>
            ))}
          </div>
        )}

        {tags.length > 0 && (
          <div className="fsec">
            <div className="fsec-h">Tags</div>
            <div className="ea-tags">
              {tags.map((t, i) => (
                <span key={i} className="ea-tag">
                  {String(t)}
                </span>
              ))}
            </div>
          </div>
        )}

        {!heroImage && !about && links.length === 0 && kvRows.length === 0 && flatRows.length === 0 && tags.length === 0 && (
          <div className="ea-empty">No details available.</div>
        )}
      </div>
    </div>
  );
}
